import { createApi, fetchBaseQuery, BaseQueryFn } from '@reduxjs/toolkit/query/react';
import { getApiConfig } from '@/config/api';
import { getRequestLanguage } from '@/utils/requestLanguage';
import type { PaymentSettings, BankAccount, ApiResponse } from '@/types';

// Create a dynamic base query that always uses the current hostname
const dynamicBaseQuery: BaseQueryFn = async (args, api, extraOptions) => {
  const config = getApiConfig();

  const baseQuery = fetchBaseQuery({
    baseUrl: config.API_URL,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('token');
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      headers.set('accept', 'application/json');
      headers.set('content-type', 'application/json');
      headers.set('accept-language', getRequestLanguage());
      return headers;
    },
  });

  return baseQuery(args, api, extraOptions);
};

export const paymentApi = createApi({
  reducerPath: 'paymentApi',
  baseQuery: dynamicBaseQuery,
  tagTypes: ['PaymentSettings'],
  endpoints: (builder) => ({
    // Get payment settings
    getPaymentSettings: builder.query<ApiResponse<PaymentSettings>, string | void>({
      query: (outletId) => {
        const params = new URLSearchParams();
        if (outletId) params.append('outletId', outletId);
        return {
          url: `/settings/payment?${params.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['PaymentSettings'],
    }),

    // Generate QRIS preview
    generateQRISPreview: builder.mutation<ApiResponse<{ qrDataUrl: string; content: string }>, { content: string }>({
      query: (data) => ({
        url: '/settings/payment/qris/preview',
        method: 'POST',
        body: data,
      }),
    }),

    // Update bank accounts
    updateBankAccounts: builder.mutation<ApiResponse<{ bankAccounts: BankAccount[] }>, { bankAccounts: BankAccount[]; outletId?: string }>({
      query: ({ bankAccounts, outletId }) => {
        const params = new URLSearchParams();
        if (outletId) params.append('outletId', outletId);
        return {
          url: `/settings/payment/bank-accounts?${params.toString()}`,
          method: 'PUT',
          body: { bankAccounts },
        };
      },
      invalidatesTags: ['PaymentSettings'],
    }),
  }),
});

export const {
  useGetPaymentSettingsQuery,
  useGenerateQRISPreviewMutation,
  useUpdateBankAccountsMutation,
} = paymentApi;
